import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/api";
import { Mail, Send, Save, Zap } from "lucide-react";
import { formatDeliveryStatusLabel, formatWebhookEventLabel } from "@/lib/labels";

const EVENT_LABELS = {
  boleto_emitido: { title: "Boleto Emitido", desc: "Envio do boleto + linha digitável + PIX ao emitir" },
  lembrete_vencimento: { title: "Lembrete antes do vencimento", desc: "Aviso N dias antes do vencimento" },
  vencimento_hoje: { title: "Vencimento hoje", desc: "Lembrete no mesmo dia do vencimento" },
  vencido: { title: "Boleto vencido", desc: "Aviso N dias após o vencimento" },
  comprovante_pago: { title: "Comprovante de pagamento", desc: "Confirmação após boleto pago" },
};

export default function ConfiguracoesEmail() {
  const [smtp, setSmtp] = useState({
    host: "smtp.gmail.com", port: 587, user: "", password: "",
    from_email: "", from_name: "", use_tls: true, active: true,
  });
  const [rules, setRules] = useState({});
  const [templates, setTemplates] = useState({});
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [logs, setLogs] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, r, l] = await Promise.all([
      api.get("/smtp"),
      api.get("/regua"),
      api.get("/regua/logs"),
    ]);
    if (s.data && s.data.host) setSmtp((current) => ({ ...current, ...s.data, password: "" }));
    setRules(r.data.rules || {});
    setTemplates(r.data.templates || {});
    setDefaultTemplates(r.data.default_templates || {});
    setLogs(l.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSmtp = async () => {
    setBusy(true);
    try { await api.put("/smtp", smtp); toast.success("SMTP salvo"); }
    catch (e) { toast.error("Erro ao salvar SMTP"); }
    finally { setBusy(false); }
  };

  const testSmtp = async () => {
    try {
      const { data } = await api.post("/smtp/testar", { to: testTo });
      if (data.ok) toast.success("E-mail de teste enviado!");
      else toast.error(`Falha: ${data.error}`);
    } catch (e) { toast.error("Erro ao testar"); }
  };

  const saveRegua = async () => {
    setBusy(true);
    try { await api.put("/regua", { rules, templates }); toast.success("Régua salva"); }
    catch (e) { toast.error("Erro ao salvar régua"); }
    finally { setBusy(false); }
  };

  const runRegua = async () => {
    try {
      const { data } = await api.post("/regua/executar");
      toast.success(`Régua executada: ${data.sent} e-mails`);
      load();
    } catch { toast.error("Erro ao executar régua"); }
  };

  return (
    <div className="space-y-6" data-testid="configuracoes-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Comunicação</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Régua de Cobrança</h1>
        <p className="text-sm text-zinc-500 mt-1">Configure SMTP + e-mails automáticos por evento do boleto.</p>
      </div>

      <Tabs defaultValue="smtp" className="w-full">
        <TabsList 
          className="rounded-sm w-full flex justify-start overflow-x-auto whitespace-nowrap" 
          data-testid="config-tabs"
        >
          <TabsTrigger value="smtp" data-testid="tab-smtp">SMTP</TabsTrigger>
          <TabsTrigger value="regua" data-testid="tab-regua">Régua</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Logs de envio</TabsTrigger>
        </TabsList>

        {/* SMTP */}
        <TabsContent value="smtp">
          <Card className="rounded-sm border border-zinc-200 shadow-none p-6 space-y-4 max-w-3xl">
            <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono uppercase tracking-widest">
              <Mail className="w-3.5 h-3.5" /> SMTP do tenant
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Host SMTP" value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} mono testid="smtp-host" />
              <Field label="Porta" type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) })} mono testid="smtp-port" />
              <Field label="Usuário (e-mail)" value={smtp.user} onChange={(e) => setSmtp({ ...smtp, user: e.target.value })} testid="smtp-user" />
              <Field label="Senha / App Password" type="password" placeholder="(em branco = manter atual)" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} mono testid="smtp-password" />
              <Field label="De (e-mail)" value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} testid="smtp-from-email" />
              <Field label="De (nome)" value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} testid="smtp-from-name" />
              <div className="flex items-center gap-3 sm:col-span-2">
                <Switch checked={smtp.use_tls} onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v })} data-testid="smtp-tls" />
                <span className="text-sm">Usar STARTTLS (recomendado p/ 587)</span>
                <Switch checked={smtp.active} onCheckedChange={(v) => setSmtp({ ...smtp, active: v })} data-testid="smtp-active" />
                <span className="text-sm">Ativo</span>
              </div>
            </div>
            <div className="border-t border-zinc-200 pt-4 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Testar conexão</div>
              <div className="flex gap-2">
                <Input placeholder="Enviar e-mail teste para…" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="rounded-sm h-10 flex-1" data-testid="smtp-test-to" />
                <Button variant="outline" onClick={testSmtp} className="rounded-sm h-10" data-testid="smtp-test-btn">
                  <Send className="w-3.5 h-3.5 mr-2" /> Enviar teste
                </Button>
              </div>
              <details className="text-xs text-zinc-500">
                <summary className="cursor-pointer">Como gerar App Password do Gmail?</summary>
                <div className="mt-2 pl-4 space-y-1">
                  <p>1. Ative verificação em 2 etapas em <code>myaccount.google.com</code>.</p>
                  <p>2. Acesse <code className="break-words">myaccount.google.com/apppasswords</code>, gere uma senha de 16 chars.</p>
                  <p>3. Cole essa senha no campo "Senha" acima (NÃO use sua senha normal).</p>
                  <p>4. Host: <code>smtp.gmail.com</code> · Porta: <code>587</code> · TLS: <b>ativado</b>.</p>
                </div>
              </details>
            </div>
            <Button onClick={saveSmtp} disabled={busy} className="rounded-sm" data-testid="smtp-save">
              <Save className="w-4 h-4 mr-2" /> {busy ? "Salvando..." : "Salvar SMTP"}
            </Button>
          </Card>
        </TabsContent>

        {/* Régua */}
        <TabsContent value="regua">
          <Card className="rounded-sm border border-zinc-200 shadow-none p-6 space-y-4 max-w-3xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono uppercase tracking-widest">
                <Zap className="w-3.5 h-3.5" /> Regras de envio
              </div>
              <Button variant="outline" size="sm" onClick={runRegua} className="rounded-sm" data-testid="regua-run-now">
                Executar agora
              </Button>
            </div>
            <div className="space-y-3">
              {Object.entries(EVENT_LABELS).map(([key, meta]) => {
                const r = rules[key] || {};
                return (
                  <div key={key} className="border border-zinc-200 rounded-sm p-3 flex flex-col sm:flex-row items-start gap-4" data-testid={`regua-rule-${key}`}>
                    <Switch
                      checked={!!r.active}
                      onCheckedChange={(v) => setRules({ ...rules, [key]: { ...r, active: v } })}
                      data-testid={`regua-toggle-${key}`}
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{meta.title}</div>
                      <div className="text-xs text-zinc-500">{meta.desc}</div>
                    </div>
                    {(key === "lembrete_vencimento" || key === "vencido") && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Dias</Label>
                        <Input
                          type="number"
                          value={r.offset_days ?? (key === "lembrete_vencimento" ? -3 : 1)}
                          onChange={(e) => setRules({ ...rules, [key]: { ...r, offset_days: parseInt(e.target.value) } })}
                          className="rounded-sm h-9 w-20 font-mono text-xs"
                          data-testid={`regua-days-${key}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button onClick={saveRegua} disabled={busy} className="rounded-sm" data-testid="regua-save">
              <Save className="w-4 h-4 mr-2" /> Salvar régua
            </Button>
          </Card>
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates">
          <Card className="rounded-sm border border-zinc-200 shadow-none p-6 space-y-4">
            <div className="text-xs text-zinc-500 mb-2">
              Use variáveis: <code className="bg-zinc-100 px-1">{"{{cliente_nome}}"}</code> · <code className="bg-zinc-100 px-1">{"{{valor}}"}</code> · <code className="bg-zinc-100 px-1">{"{{vencimento}}"}</code> · <code className="bg-zinc-100 px-1">{"{{linha_digitavel}}"}</code> · <code className="bg-zinc-100 px-1">{"{{pix_copia_cola}}"}</code> · <code className="bg-zinc-100 px-1">{"{{pdf_url}}"}</code> · <code className="bg-zinc-100 px-1">{"{{numero_documento}}"}</code> · <code className="bg-zinc-100 px-1">{"{{empresa}}"}</code> · <code className="bg-zinc-100 px-1">{"{{data_pagamento}}"}</code>
            </div>
            {Object.entries(EVENT_LABELS).map(([key, meta]) => {
              const t = templates[key] || defaultTemplates[key] || { subject: "", html: "" };
              return (
                <div key={key} className="border border-zinc-200 rounded-sm p-4 space-y-2" data-testid={`tpl-${key}`}>
                  <div className="text-sm font-semibold">{meta.title}</div>
                  <Input
                    placeholder="Assunto"
                    value={t.subject}
                    onChange={(e) => setTemplates({ ...templates, [key]: { ...t, subject: e.target.value } })}
                    className="rounded-sm font-mono text-xs"
                    data-testid={`tpl-${key}-subject`}
                  />
                  <Textarea
                    rows={6}
                    value={t.html}
                    onChange={(e) => setTemplates({ ...templates, [key]: { ...t, html: e.target.value } })}
                    className="rounded-sm font-mono text-xs"
                    data-testid={`tpl-${key}-html`}
                  />
                </div>
              );
            })}
            <Button onClick={saveRegua} disabled={busy} className="rounded-sm" data-testid="tpl-save">
              <Save className="w-4 h-4 mr-2" /> Salvar templates
            </Button>
          </Card>
        </TabsContent>

        {/* Logs */}
        <TabsContent value="logs">
          <Card className="rounded-sm border border-zinc-200 shadow-none overflow-x-auto">
            <table className="tbl" data-testid="email-logs-table">
              <thead><tr><th>Quando</th><th>Para</th><th>Evento</th><th>Assunto</th><th>Status</th><th>Erro</th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-sm text-zinc-500">Nenhum e-mail enviado ainda.</td></tr>}
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="font-mono text-xs">{formatDateTime(l.sent_at)}</td>
                    <td className="font-mono text-xs">{l.to}</td>
                    <td className="text-xs" title={l.evento}>{formatWebhookEventLabel(l.evento)}</td>
                    <td className="text-xs">{l.subject}</td>
                    <td>
                      {l.status === "sent" && <span className="text-[10px] font-mono uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">{formatDeliveryStatusLabel(l.status)}</span>}
                      {l.status === "failed" && <span className="text-[10px] font-mono uppercase text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-sm">{formatDeliveryStatusLabel(l.status)}</span>}
                    </td>
                    <td className="text-xs text-red-600">{l.error || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, mono, className = "", testid, ...props }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{label}</Label>
      <Input {...props} className={`rounded-sm h-10 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  );
}
